import React from "react";

// Find every case-insensitive occurrence of each phrase in `prompt`.
// Returns the match ranges (half-open [start, end)) and the phrases that
// never appeared (rendered as plain bullets — the graceful fallback).
export function findPhraseRanges(prompt, phrases) {
  const lower = prompt.toLowerCase();
  const ranges = [];
  const unmatched = [];
  (phrases || []).forEach((phrase) => {
    const needle = (phrase || "").toLowerCase();
    if (!needle) return;
    let from = 0;
    let found = false;
    for (;;) {
      const idx = lower.indexOf(needle, from);
      if (idx === -1) break;
      ranges.push([idx, idx + needle.length]);
      from = idx + needle.length;
      found = true;
    }
    if (!found) unmatched.push(phrase);
  });
  return { ranges, unmatched };
}

// Turn match ranges into ordered { text, violated } segments, merging overlaps.
export function buildSegments(prompt, ranges) {
  if (!ranges.length) return [{ text: prompt, violated: false }];
  const sorted = [...ranges].sort((a, b) => a[0] - b[0]);
  const merged = [];
  sorted.forEach(([s, e]) => {
    const last = merged[merged.length - 1];
    if (last && s <= last[1]) last[1] = Math.max(last[1], e);
    else merged.push([s, e]);
  });
  const segments = [];
  let cursor = 0;
  merged.forEach(([s, e]) => {
    if (s > cursor) segments.push({ text: prompt.slice(cursor, s), violated: false });
    segments.push({ text: prompt.slice(s, e), violated: true });
    cursor = e;
  });
  if (cursor < prompt.length) segments.push({ text: prompt.slice(cursor), violated: false });
  return segments;
}

const isAdherence = (value) =>
  value && typeof value === "object" && !Array.isArray(value) &&
  typeof value.prompt === "string" && Array.isArray(value.issues);

function AdherenceField({ value }) {
  const phrases = value.issues
    .map((i) => (typeof i === "string" ? i : i && i.phrase))
    .filter((p) => typeof p === "string" && p.trim());
  const { ranges } = findPhraseRanges(value.prompt, phrases);
  const segments = buildSegments(value.prompt, ranges);
  const notes = value.issues
    .map((i) => (typeof i === "string" ? i : (i && (i.note || i.phrase))))
    .filter((n) => typeof n === "string" && n.trim());
  return (
    <div>
      <p className="adherence-prompt mb-1">
        {segments.map((seg, idx) =>
          seg.violated
            ? (
              <span
                key={idx}
                className="adherence-violation"
                style={{ color: "#dc3545", fontWeight: 600, textDecoration: "underline" }}
              >
                {seg.text}
              </span>
            )
            : <span key={idx}>{seg.text}</span>
        )}
      </p>
      {notes.length > 0 && (
        <ul className="adherence-issues mb-0">
          {notes.map((n, idx) => <li key={idx}>{n}</li>)}
        </ul>
      )}
    </div>
  );
}

function FieldValue({ value }) {
  if (isAdherence(value)) return <AdherenceField value={value} />;
  if (Array.isArray(value)) {
    return (
      <ul className="mb-0">
        {value.map((item, idx) => (
          <li key={idx}>{typeof item === "string" ? item : JSON.stringify(item)}</li>
        ))}
      </ul>
    );
  }
  if (typeof value === "string") return <p className="mb-0">{value}</p>;
  if (value && typeof value === "object") {
    return (
      <pre
        className="variant-field-json mb-0"
        style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
      >
        {JSON.stringify(value, null, 2)}
      </pre>
    );
  }
  if (value !== null && value !== undefined) return <p className="mb-0">{String(value)}</p>;
  return null;
}

// Renders the configured display fields under one variant's media item.
export default function VariantDisplayFields({ fields }) {
  if (!fields || !fields.length) return null;
  return (
    <div className="variant-display-fields mt-2 text-start">
      {fields.map((f) => (
        <div key={f.field} className="mb-2">
          {f.label ? <div className="variant-field-label small text-muted">{f.label}</div> : null}
          <FieldValue value={f.value} />
        </div>
      ))}
    </div>
  );
}
